import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Button } from '@/components/atoms/Buttons';
import FormField, { useFormFieldControl } from '@/components/atoms/FormField';
import fieldStyles from '@/components/atoms/FormField/FormField.module.scss';
import { SCADA_ITEMS_MAX } from '@/domain/providerConfig';

import { type ItemIdBinding, ScadaItemRow } from './ScadaItemRow';
import { type ScadaRows, useScadaRows } from './useScadaRows';

import styles from './ProviderSchemaField.module.scss';

export type { ItemIdBinding };

export interface ScadaItemsEditorProps {
    id: string;
    label: string;
    /** Shown after the label in a lighter weight, as FormField shows a suffix. */
    labelSuffix?: ReactNode;
    value: string;
    onChange: (text: string) => void;
    signals: readonly string[];
    disabled?: boolean;
    /** Help under the list, as FormField shows a hint. */
    hint?: ReactNode;
    /** Notes elsewhere that describe the list besides its hint and error. */
    describedBy?: string;
    /** The list's error: the rows at fault are marked, and the message shows under the list. */
    error?: string | null;
    /** Rendered under each row's item id, e.g. a catalog picker. */
    itemPicker?: (binding: ItemIdBinding) => ReactNode;
    /** Told whether the list's error is held back until a new row is filled in or left. */
    onHeldBackChange?: (held: boolean) => void;
}

interface ScadaFieldsetProps {
    label: string;
    labelSuffix?: ReactNode;
    describedBy?: string;
    disabled: boolean;
    list: ScadaRows;
    signals: readonly string[];
    itemPicker?: (binding: ItemIdBinding) => ReactNode;
}

/**
 * The entries as one group named by its legend. It sits in a FormField for the hint and error,
 * which describe the group through the field's context.
 */
function ScadaFieldset({
    label,
    labelSuffix,
    describedBy,
    disabled,
    list,
    signals,
    itemPicker,
}: ScadaFieldsetProps) {
    const { t } = useTranslation();
    const { id, 'aria-describedby': described } = useFormFieldControl({
        'aria-describedby': describedBy,
    });
    return (
        <fieldset id={id} className={styles.scada} aria-describedby={described}>
            {/* A legend, not a label: it names the group. It reads as every field's label. */}
            <legend className={classNames(fieldStyles.label, styles.legend)}>
                {label}
                {labelSuffix !== undefined && (
                    <span className={fieldStyles.labelSuffix}>{labelSuffix}</span>
                )}
            </legend>
            {list.rows.length === 0 && (
                <p className={styles.note}>{t('providerConfig.scada.empty')}</p>
            )}
            {list.rows.map((row, index) => (
                <ScadaItemRow
                    key={list.keys[index]}
                    row={row}
                    rowId={`${id}-${list.keys[index]}`}
                    rowName={t('providerConfig.scada.row', { number: index + 1 })}
                    fault={list.faultAt(index)}
                    signals={signals}
                    disabled={disabled}
                    itemPicker={itemPicker}
                    onUpdate={(change) => list.update(index, change)}
                    onRemove={() => list.remove(index)}
                    onLeave={() => list.leave(index)}
                />
            ))}
            <Button
                variant="secondary"
                className={styles.listAction}
                disabled={disabled || list.isFull}
                onClick={list.addRow}>
                {t('providerConfig.scada.add')}
            </Button>
            {list.isFull && (
                <p className={styles.note}>
                    {t('providerConfig.scada.full', { max: SCADA_ITEMS_MAX })}
                </p>
            )}
        </fieldset>
    );
}

function ScadaItemsEditor({
    id,
    label,
    labelSuffix,
    value,
    onChange,
    signals,
    disabled = false,
    hint,
    describedBy,
    error = null,
    itemPicker,
    onHeldBackChange,
}: ScadaItemsEditorProps) {
    const list = useScadaRows({ id, value, onChange, signals, error });
    // The caller passes a new function each render; only a change of state is news.
    const report = useRef(onHeldBackChange);
    useLayoutEffect(() => {
        report.current = onHeldBackChange;
    });
    // Before paint, so the card never shows "invalid" for a frame before it hears of the hold.
    useLayoutEffect(() => {
        report.current?.(list.heldBack);
    }, [list.heldBack]);
    useLayoutEffect(() => () => report.current?.(false), []);

    return (
        <FormField htmlFor={id} hint={hint} error={list.showError ? error : null}>
            <ScadaFieldset
                label={label}
                labelSuffix={labelSuffix}
                describedBy={describedBy}
                disabled={disabled}
                list={list}
                signals={signals}
                itemPicker={itemPicker}
            />
        </FormField>
    );
}

export default ScadaItemsEditor;
export { ScadaItemsEditor };
